import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import StruxhubUserSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubUserSelector'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { EMPTY_STRING, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import StruxhubTagValueSelector from '../../../StruxhubInputs/StruxhubSelector/StruxhubTagValueSelector'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'

const MAX_SHOWED_USERS_TAG = 2

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean
  shouldHideResponsible: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number

  projectMembersStore?: ProjectMembersStore
}

@inject('projectMembersStore')
@observer
export default class PermitFormUserField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      typeField,
      isViewMode,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={null}
        viewModeRenderer={this.renderViewMode}
      >
        {this.renderUsers()}
      </PermitBaseFormField>
    )
  }

  private renderUsers(): JSX.Element {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { caption, isMandatory } = typeField
    const { isFieldChanged } = fieldsStore

    if (this.fieldValues.length > 1)
      return (
        <StruxhubTagValueSelector
          label={caption}
          isRequired={isMandatory}
          isChanged={isFieldChanged(typeField.id)}
          onClick={this.onOpenUserModal}
          values={this.userTags}
          shouldUseDefaultColor
          isShownAsTag
        />
      )

    const testId = `workflow-user-selector-${caption
      .toLowerCase()
      .replace(/\s+/g, '-')}`

    return (
      <StruxhubUserSelector
        className="overflow-hidden"
        label={caption}
        isRequired={isMandatory}
        isChanged={isFieldChanged(
          typeField.id,
          0,
          tableId,
          tableRowIndex,
          parentInstanceId,
          parentValueIndex,
        )}
        onClick={this.onOpenUserModal}
        value={this.fieldValues[0]}
        testId={testId}
      />
    )
  }

  private renderViewMode = (value: string): JSX.Element => {
    const user = this.localProps.projectMembersStore.getById(value)

    if (!user) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    return <UserProfilePreview user={user} />
  }

  private get fieldValues(): string[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const values = fieldsStore.getFieldValues<string>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : ['']
  }

  private onOpenUserModal = () => {
    const {
      store,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    store.setSelectedField(
      typeField,
      0,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )
    store.setSelectedOnChangeHandler(this.onApply.bind(null))
  }

  private onApply = (newValue: string[]) => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeFieldValue, changeTableFieldValue } = fieldsStore

    if (typeField.isMultiple) {
      return fieldsStore.changeMultipleFieldValues(
        typeField,
        newValue,
        parentInstanceId,
        parentValueIndex,
      )
    }

    const singleValue = newValue[0] || EMPTY_STRING
    if (!this.isTableField) {
      return changeFieldValue(
        typeField,
        0,
        singleValue,
        parentInstanceId,
        parentValueIndex,
      )
    }

    changeTableFieldValue(
      tableId,
      tableRowIndex,
      typeField,
      0,
      singleValue,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private get isTableField(): boolean {
    return !!this.props.tableId
  }

  private get userTags(): { name: string }[] {
    const { projectMembersStore } = this.props

    const values = this.fieldValues.slice(0, MAX_SHOWED_USERS_TAG).map(u => ({
      name: projectMembersStore.hasById(u)
        ? projectMembersStore.getById(u).fullName
        : Localization.translator.unknownUser,
    }))

    const restCount = this.fieldValues.slice(MAX_SHOWED_USERS_TAG).length
    if (restCount) {
      values.push({
        name: `+ ${Localization.translator.xUsers(restCount)} `,
      })
    }

    return values
  }
}
